import { NetworkIntegrityAggregate, NetworkIntegrityAggregateTableRow } from "@app/projects/network-assessment/network-integrity/models";
import { JSX, useContext, useEffect, useMemo, useState } from "react";
import { NetworkIntegrityAggregateTable, NetworkIntegrityWidgetInfoFlyout, OptionalExclusionsDescription, WidgetIcon, WidgetMenu } from ".";
import { NetworkIntegrityActionStatus, NetworkIntegrityProviderTypes } from "@app/projects/network-assessment/network-integrity/filters/networkIntegrityFilterConstants";
import { NetworkIntegrityContext } from "@app/projects/network-assessment/network-integrity/NetworkIntegrityContext";
import NetworkIntegrityService from "@app/projects/network-assessment/network-integrity/networkIntegrityService";
import { NetworkIntegrityAggregateGroup, NetworkIntegrityAggregateKey, NetworkIntegrityJobStatus, ProjectExclusionType } from "@app/projects/enumerators";
import { Link } from "react-router-dom";
import { UserContext } from "@app/user/UserContext";
import Roles from "@app/user/roles";
import { getPendoClassNameForAggregateGroup, getPendoClassNameForAggregateKey } from "@app/projects/network-assessment/network-integrity/helpers/networkIntegrityPendoHelper";
import { WidgetLoading, WidgetHeader, Widget, WidgetBody, WidgetFooter } from "./Widget";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { optionExclusionDisplayKeyToAggregateKey } from "@app/projects/network-assessment/network-integrity/helpers/networkIntegrityDisplayHelper";

const ProjectOptionExclusionsWidget = (): JSX.Element => {
  const networkIntegrityContext = useContext(NetworkIntegrityContext);
  const project = networkIntegrityContext.project!;
  const ffContext = useContext(FeatureFlagContext);
  const networkIntegrityService = useMemo(() => new NetworkIntegrityService(), []);

  const [optionalExclusionsResult, setOptionalExclusionsResult] = useState<NetworkIntegrityAggregate[]>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const user = useContext(UserContext);

  const title = 'Project Option Exclusions';
  const titleIcon = WidgetIcon.Info;

  useEffect(() => {
    let cancelled = false;
    setOptionalExclusionsResult(null);
    async function fetchOptionalExclusionResults() {
      try {
        if (networkIntegrityContext.projectRunConfigurationId) {
          const result = await networkIntegrityService.getOptionalExclusionResults(project.projectId, networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes);
          if (!cancelled) setOptionalExclusionsResult(result);
        }
      } finally {
        if (!cancelled) setLoading(false);
      };
    };

    if (networkIntegrityContext.isJobStatusLoading === false && networkIntegrityContext.jobStatus == NetworkIntegrityJobStatus.Finished) {
      setLoading(true);
      fetchOptionalExclusionResults();
    }

    return () => { cancelled = true; };
  }, [networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, networkIntegrityContext.jobStatus, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes]);

  const generateProviderCellContent = (aggregateKey: NetworkIntegrityAggregateKey, afterTermedCount: boolean = false): JSX.Element => {
    const count = afterTermedCount ? optionalExclusionsResult.find(a => a.aggregateKey == aggregateKey)?.afterTermedCount : optionalExclusionsResult.find(a => a.aggregateKey == aggregateKey)?.count;
    const excluded = () => {
      switch (aggregateKey) {
        case NetworkIntegrityAggregateKey.DeactivatedNpi:
          if (networkIntegrityContext.projectRunConfiguration?.networkExclusions?.includes(ProjectExclusionType.DeactivatedFacility)
            && networkIntegrityContext.projectRunConfiguration.networkExclusions?.includes(ProjectExclusionType.DeactivatedProvider))
            return true;
            break;
        case NetworkIntegrityAggregateKey.InvalidNpi:
          if (networkIntegrityContext.projectRunConfiguration?.networkExclusions?.includes(ProjectExclusionType.InvalidNppesNpiFacility)
            && networkIntegrityContext.projectRunConfiguration.networkExclusions?.includes(ProjectExclusionType.InvalidNppesNpiProvider))
            return true;
            break;
        case NetworkIntegrityAggregateKey.MissingNpi:
          if (networkIntegrityContext.projectRunConfiguration?.networkExclusions?.includes(ProjectExclusionType.MissingNpiFacility)
            && networkIntegrityContext.projectRunConfiguration.networkExclusions?.includes(ProjectExclusionType.MissingNpiProvider))
            return true;
            break;
        case NetworkIntegrityAggregateKey.OigExclusion:
          if (networkIntegrityContext.projectRunConfiguration?.networkExclusions?.includes(ProjectExclusionType.OIGExclusion))
            return true;
            break;
        case NetworkIntegrityAggregateKey.MedicareOptOut:
          if (networkIntegrityContext.projectRunConfiguration?.networkExclusions?.includes(ProjectExclusionType.MedicareOptOut))
            return true;
            break;
        case NetworkIntegrityAggregateKey.MedicarePrecluded:
          if (networkIntegrityContext.projectRunConfiguration?.networkExclusions?.includes(ProjectExclusionType.Precluded))
            return true;
            break;
        default:
          return false;
      }
    }

    const defaultOutput = (!count) ? 
      <span>0</span> :
      excluded() && (networkIntegrityContext.providerType == NetworkIntegrityProviderTypes.Servicing
        || networkIntegrityContext.providerType == NetworkIntegrityProviderTypes.ProspectServicing) ?
        <span>{count.toLocaleString()}</span> :
        <Link className={`${getPendoClassNameForAggregateKey(aggregateKey)}`}
          to={{
            pathname: `/projects/${project.projectId}/${networkIntegrityContext.projectRunConfigurationId}/network-integrity/providers/${networkIntegrityContext.providerType}/${NetworkIntegrityAggregateGroup.OptionalExclusions}/${encodeURIComponent(aggregateKey)}`,
            state: {
              initialActionStatus: afterTermedCount ? NetworkIntegrityActionStatus.NoActionsApplied : undefined,
              initialRegionSetIds: networkIntegrityContext.regionSetIds,
              initialSpecialtyCodes: networkIntegrityContext.specialtyCodes,
              runConfiguration: networkIntegrityContext.projectRunConfiguration
            }
          }}>
            {count.toLocaleString()}
        </Link>;
      
    if (networkIntegrityContext.providerType === NetworkIntegrityProviderTypes.Network
        || networkIntegrityContext.providerType === NetworkIntegrityProviderTypes.Prospect) {
      return defaultOutput;
    }

    const excludedOutput = <span>Excluded</span>;
    return excluded() ? excludedOutput : defaultOutput;
  }

  if (networkIntegrityContext.isLoading || loading || !networkIntegrityContext.jobStatus)
    return (<WidgetLoading><WidgetHeader title={title} titleIcon={titleIcon} /></WidgetLoading>);

  let rows : NetworkIntegrityAggregateTableRow[] = [...optionExclusionDisplayKeyToAggregateKey.keys()].map((key) => {
    return {aggregateKey: key}
  });

  if(optionalExclusionsResult)
  {
    // filter out rows the user does not have access to view
    rows = rows.filter((row) => {
      const value = optionExclusionDisplayKeyToAggregateKey.get(row.aggregateKey);
      if (value === NetworkIntegrityAggregateKey.OigExclusion && !user.roles.includes(Roles.OIG))
        return false;
      if (value === NetworkIntegrityAggregateKey.MedicareOptOut && !user.roles.includes(Roles.MEDICARE_OPT_OUTS))
        return false;
      if (value === NetworkIntegrityAggregateKey.MedicarePrecluded && !user.roles.includes(Roles.PRECLUDED_PROVIDER_USER))
        return false;
      return true;
    });

    rows.forEach((row) => {
      const niAggKey = optionExclusionDisplayKeyToAggregateKey.get(row.aggregateKey);
      const resultEntry = optionalExclusionsResult.find(a => a.aggregateKey === niAggKey);
      row.afterTermedCount = generateProviderCellContent(niAggKey, true);
      row.count = generateProviderCellContent(niAggKey);
      row.rawAfterTermedCount = resultEntry?.afterTermedCount ?? undefined;
      row.rawCount = resultEntry?.count ?? undefined;
    });
  }

  const footer =
    ffContext.enabledFeatureFlags.has(Features.DISPLAY_NETWORK_INTEGRITY_TOTAL_AFTER_TERMED) && 
    <WidgetFooter>
      <Link className={getPendoClassNameForAggregateGroup(NetworkIntegrityAggregateGroup.OptionalExclusions, "view-all")}
        to={{
          pathname: `/projects/${project.projectId}/${networkIntegrityContext.projectRunConfigurationId}/network-integrity/all/${NetworkIntegrityAggregateGroup.OptionalExclusions}`,
          state: {
            initialProviderType: networkIntegrityContext.providerType,
            initialRegionSetIds: networkIntegrityContext.regionSetIds,
            initialSpecialtyCodes: networkIntegrityContext.specialtyCodes,
            runConfiguration: networkIntegrityContext.projectRunConfiguration
          }
        }}>
          View All
      </Link>
    </WidgetFooter>;

  return (
    <Widget>
      <WidgetHeader title={title} titleIcon={titleIcon}>
        <WidgetMenu>
            <NetworkIntegrityWidgetInfoFlyout title="Project Option Exclusions" description={<OptionalExclusionsDescription/>}/>
            {optionalExclusionsResult && <Link
              className="qes-button qes-button-secondary"
              id="network-integrity-providers__download-report-link"
              to={{
                pathname: `/projects/${project.projectId}/${networkIntegrityContext.projectRunConfigurationId}/network-integrity/reports/${networkIntegrityContext.providerType}/${NetworkIntegrityAggregateGroup.OptionalExclusions}`,
                state: {
                  reportRowCount: optionalExclusionsResult.reduce((sum, current) => sum + current.count, 0),
                  runConfiguration: networkIntegrityContext.projectRunConfiguration
                }
              }}
              type="button" >
              Create All Report
            </Link>}
          </WidgetMenu>
      </WidgetHeader>
      <WidgetBody>
        <NetworkIntegrityAggregateTable aggregateHeader="Alert" aggregateCountHeader="Unique Access Points" rows={rows} />
      </WidgetBody>
      {footer}
    </Widget>);
}

export { ProjectOptionExclusionsWidget };